import { ORDINAL_NUMBERS_SUFFIX_MAP } from '$app-web/constants';

const BASE_POUND = 2.20462262185 as const;
const BASE_INCH = 2.54 as const;

// Maximum safe number for display/input without potential issues in JSON serialization
// Numbers with absolute value >= this threshold will be shown in scientific notation
export const MAX_DISPLAY_NUMBER = 1e307;

export function roundNumber(value: number, precision: number) {
    const multiplier = 10 ** precision;

    return Math.round(value * multiplier) / multiplier;
}

export function centimetersToFeetAndInches(length: number) {
    const totalInches = roundNumber(length / BASE_INCH, 4);
    const feet = Math.floor(totalInches / 12);
    const inches = Math.floor(totalInches % 12);

    return { feet, inches };
}

export function feetAndInchesToCentimeters(feet: number, inches: number) {
    const totalInches = feet * 12 + inches;
    const centimeters = roundNumber(totalInches * BASE_INCH, 4);

    return centimeters;
}

export function kilogramToPound(kg: number) {
    const convertedValue = kg * BASE_POUND;

    return roundNumber(convertedValue, 4);
}

export function poundToKilogram(pound: number) {
    const convertedValue = pound / BASE_POUND;

    return roundNumber(convertedValue, 4);
}

export function getOrdinalNumber(number: number, locale = 'en-US') {
    const availableLocales = Object.keys(ORDINAL_NUMBERS_SUFFIX_MAP);
    const targetLocale = availableLocales.includes(locale) ? locale : 'en-US';
    const ordinalRules = new Intl.PluralRules(targetLocale, { type: 'ordinal' });
    const pluralRule = ordinalRules.select(number);
    const ordinalSuffixMap = ORDINAL_NUMBERS_SUFFIX_MAP[targetLocale];
    const suffix = ordinalSuffixMap[pluralRule];

    return `${number}${suffix}`;
}

export function getPercentage(value: number, total: number, precision = 0) {
    return roundNumber((value / total) * 100, precision);
}

export function getAchievementFormattedScore(score: number | string) {
    const numScore = typeof score === 'string' ? parseFloat(score) : score;
    return numScore % 1 === 0 ? numScore.toString() : numScore.toFixed(2);
}

function toSignificantDigits(value: number, digits: number): string {
    const result = parseFloat(value.toPrecision(digits));
    return String(result);
}

function roundScaled(scaled: number): number {
    return scaled >= 100 ? Math.round(scaled) : parseFloat(scaled.toPrecision(3));
}

function formatWithSuffix(value: number, divisor: number, suffix: string): string {
    return `${roundScaled(value / divisor)}${suffix}`;
}

export function formatCompactScore(score: number): string {
    if (!Number.isFinite(score) || score < 0) return '0';

    if (score >= 1_000_000_000) return formatWithSuffix(score, 1_000_000_000, 'B');
    if (score >= 1_000_000) {
        if (roundScaled(score / 1_000_000) >= 1000) return formatWithSuffix(score, 1_000_000_000, 'B');
        return formatWithSuffix(score, 1_000_000, 'M');
    }
    if (score >= 100_000) {
        if (roundScaled(score / 1_000) >= 1000) return formatWithSuffix(score, 1_000_000, 'M');
        return formatWithSuffix(score, 1_000, 'K');
    }
    return toSignificantDigits(score, 5);
}

// Formats number for display - uses scientific notation for very large/small numbers
export function formatNumberForDisplay(value: number | ''): string {
    if (value === '') return '';
    if (typeof value !== 'number') return String(value);
    if (Number.isNaN(value) || !Number.isFinite(value)) return '';
    if (value === 0) return '0';

    const absValue = Math.abs(value);
    if (absValue >= 1_000_000 || absValue < 0.000001) {
        return value.toExponential();
    }
    return value.toString();
}

// Converts number to full decimal string (no scientific notation) for editing
export function toFullNumberString(value: number | ''): string {
    if (value === '') return '';
    if (typeof value !== 'number' || !Number.isFinite(value)) return '';
    return value.toLocaleString('en-US', { maximumFractionDigits: 20, useGrouping: false });
}
